import React, { createContext, useState, useReducer, useMemo } from "react";
import { GroupMetric, MetricReport, Query, stringifyQuery, groupMetricFor } from "../lib/api/charts";
import { Loading } from "./base";

type GroupMetricSet = { metric: GroupMetric; loading: Loading };

export type GroupMetricContextState = {
  metric: {
    [index: string]: GroupMetricSet;
  };
  getGroupMetric(report: MetricReport, query: Query): Partial<GroupMetricSet>;
};

export const GroupMetricContext = createContext<GroupMetricContextState>({
  metric: {},
  getGroupMetric: () => ({}),
});

type QueryAction = {
  type: "PATCH_METRIC";
  id: string;
  data: Partial<GroupMetricSet>;
};

const defaultValue = {
  value: 0,
  formattedValue: "",
};

const defaultGroupMetric: GroupMetricSet = {
  metric: {
    title: "",
    actualTitle: "",
    classification: "income",
    values: [],
    dealers: [],
    dealerNames: [],
    benchmark: defaultValue,
    median: defaultValue,
    target: defaultValue,
    formats: { pos: "", neg: "" },
  },
  loading: { loaded: false, loading: true, error: null },
};

export const GroupMetricProvider = ({ children }) => {
  const memo = (report: MetricReport, query: Query) => `${report}-${stringifyQuery(query)}`;

  const [metric, dispatchGroupMetric] = useReducer((state: { [index: string]: GroupMetricSet }, action: QueryAction) => {
    switch (action.type) {
      case "PATCH_METRIC":
        return {
          ...state,
          [action.id]: {
            ...state[action.id],
            ...action.data,
          },
        };
      default:
        return state;
    }
  }, {});

  const value = useMemo(
    () => ({
      metric,
      getGroupMetric: (report: MetricReport, query: Query) => {
        const id = memo(report, query);
        const set = metric[id];

        if (set) {
          return set;
        }

        dispatchGroupMetric({
          type: "PATCH_METRIC",
          id,
          data: defaultGroupMetric,
        });

        groupMetricFor(query, report)
          .then(metric => {
            dispatchGroupMetric({
              type: "PATCH_METRIC",
              id,
              data: {
                metric: metric,
                loading: {
                  loaded: true,
                  loading: false,
                  error: null,
                },
              },
            });
          })
          .catch(e => {
            dispatchGroupMetric({
              type: "PATCH_METRIC",
              id,
              data: {
                loading: {
                  loaded: false,
                  loading: false,
                  error: e,
                },
              },
            });
          });

        return defaultGroupMetric;
      },
    }),
    [metric],
  );
  return <GroupMetricContext.Provider value={value}>{children}</GroupMetricContext.Provider>;
};
